import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { attributeService } from '../../services/attributeService';
import { productService } from '../../services/productService';
import { ProductCategory } from '../../types';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  code: 30,
  name: 100,
  description: 200,
  unit: 20,
  options: 2000,
};

const DATA_TYPES = ['Text', 'Number', 'Decimal', 'Boolean', 'Date', 'Select'] as const;

interface AttributeForm {
  code: string;
  name: string;
  description: string;
  dataType: string;
  options: string; // comma-separated; only for Select
  unit: string;
  appliesToAllProducts: boolean;
  categoryIds: number[];
  isRequired: boolean;
}

interface FieldErrors {
  code?: string;
  name?: string;
  description?: string;
  options?: string;
  categoryIds?: string;
}

const AttributeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [formData, setFormData] = useState<AttributeForm>({
    code: '',
    name: '',
    description: '',
    dataType: 'Text',
    options: '',
    unit: '',
    appliesToAllProducts: true,
    categoryIds: [],
    isRequired: false,
  });

  useEffect(() => {
    productService.getCategories({ pageSize: 100 })
      .then((res) => setCategories((res.data.data as ProductCategory[]) ?? []))
      .catch(() => setCategories([]));
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleScopeChange = (appliesToAllProducts: boolean) => {
    setFormData((prev) => ({ ...prev, appliesToAllProducts }));
    setFieldErrors((prev) => ({ ...prev, categoryIds: undefined }));
  };

  const handleCategoryToggle = (categoryId: number) => {
    setFormData((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
    setFieldErrors((prev) => ({ ...prev, categoryIds: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.code.trim()) {
      errors.code = t('attribute.formValidation.codeRequired');
      isValid = false;
    } else if (formData.code.length > MAX_LENGTHS.code) {
      errors.code = t('attribute.formValidation.codeMaxLength');
      isValid = false;
    }

    if (!formData.name.trim()) {
      errors.name = t('attribute.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('attribute.formValidation.nameMaxLength');
      isValid = false;
    }

    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('attribute.formValidation.descriptionMaxLength');
      isValid = false;
    }

    if (formData.dataType === 'Select' && formData.options.split(',').filter((o) => o.trim()).length === 0) {
      errors.options = t('attribute.formValidation.optionsRequired');
      isValid = false;
    }

    if (!formData.appliesToAllProducts && formData.categoryIds.length === 0) {
      errors.categoryIds = t('attribute.formValidation.categoriesRequired');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setLoading(true);
    try {
      await attributeService.createDefinition({
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        dataType: formData.dataType,
        options: formData.dataType === 'Select'
          ? formData.options.split(',').map((o) => o.trim()).filter(Boolean)
          : undefined,
        unit: formData.unit.trim() || undefined,
        appliesToAllProducts: formData.appliesToAllProducts,
        categoryIds: formData.appliesToAllProducts ? [] : formData.categoryIds,
        isRequired: formData.isRequired,
        isActive: true,
      });
      navigate('/products/attributes', { state: { message: t('attribute.createSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('attribute.failedToCreate')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/attributes')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('attribute.newAttribute')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/attributes')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('attribute.createTitle')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label className="form-label">
                      {t('attribute.code')} <span className="required">*</span>
                      <span className="char-counter">{formData.code.length}/{MAX_LENGTHS.code}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.code}
                      placeholder={t('attribute.codePlaceholder')}
                    />
                    {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    {!fieldErrors.code && <small className="form-helper">{t('attribute.codeHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('attribute.name')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                      placeholder={t('attribute.name')}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('attribute.nameHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('attribute.dataType')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="dataType"
                      value={formData.dataType}
                      onChange={handleInputChange}
                    >
                      {DATA_TYPES.map((dt) => (
                        <option key={dt} value={dt}>{t(`attribute.dataTypes.${dt.toLowerCase()}`)}</option>
                      ))}
                    </select>
                    <small className="form-helper">{t('attribute.dataTypeHint')}</small>
                  </div>

                  {formData.dataType === 'Select' && (
                    <div className="form-group">
                      <label className="form-label">
                        {t('attribute.options')} <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${fieldErrors.options ? 'is-invalid' : ''}`}
                        name="options"
                        value={formData.options}
                        onChange={handleInputChange}
                        maxLength={MAX_LENGTHS.options}
                        placeholder={t('attribute.optionsPlaceholder')}
                      />
                      {fieldErrors.options && <small className="form-error">{fieldErrors.options}</small>}
                      {!fieldErrors.options && <small className="form-helper">{t('attribute.optionsHint')}</small>}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">
                      {t('attribute.unit')}
                      <span className="char-counter">{formData.unit.length}/{MAX_LENGTHS.unit}</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.unit}
                      placeholder={t('attribute.unitPlaceholder')}
                    />
                    <small className="form-helper">{t('attribute.unitHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('attribute.scope')} <span className="required">*</span>
                    </label>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="scope-all"
                        name="scope"
                        checked={formData.appliesToAllProducts}
                        onChange={() => handleScopeChange(true)}
                      />
                      <label className="form-check-label" htmlFor="scope-all">
                        {t('attribute.scopeAllProducts')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="scope-categories"
                        name="scope"
                        checked={!formData.appliesToAllProducts}
                        onChange={() => handleScopeChange(false)}
                      />
                      <label className="form-check-label" htmlFor="scope-categories">
                        {t('attribute.scopeCategories')}
                      </label>
                    </div>
                    {!fieldErrors.categoryIds && <small className="form-helper">{t('attribute.scopeHint')}</small>}

                    {!formData.appliesToAllProducts && (
                      <div
                        className={`attribute-category-picker${fieldErrors.categoryIds ? ' is-invalid' : ''}`}
                        style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          border: '1px solid var(--ims-border, #dee2e6)',
                          borderRadius: '8px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                          gap: '6px',
                        }}
                      >
                        {categories.length === 0 && (
                          <small className="form-helper">{t('attribute.noCategories')}</small>
                        )}
                        {categories.map((cat) => (
                          <div className="form-check" key={cat.id}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`attr-cat-${cat.id}`}
                              checked={formData.categoryIds.includes(cat.id)}
                              onChange={() => handleCategoryToggle(cat.id)}
                            />
                            <label className="form-check-label" htmlFor={`attr-cat-${cat.id}`}>
                              {cat.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    {fieldErrors.categoryIds && <small className="form-error">{fieldErrors.categoryIds}</small>}
                  </div>

                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="attr-required"
                        checked={formData.isRequired}
                        onChange={(e) => setFormData((prev) => ({ ...prev, isRequired: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="attr-required">
                        {t('attribute.required')}
                      </label>
                    </div>
                    <small className="form-helper">{t('attribute.requiredHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('attribute.scope')}</strong>
                  <p>{t('attribute.help.scope')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('attribute.dataType')}</strong>
                  <p>{t('attribute.help.dataType')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('attribute.required')}</strong>
                  <p>{t('attribute.help.required')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AttributeCreatePage;
